import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_ORIGIN } from '../../config';

/** How an avatar renders — uploads stream through a gated same-origin URL. */
export interface AvatarView {
  kind: 'seal' | 'upload' | 'face';
  url: string;
}

/**
 * The signed-in person's own profile. `handle` is null until claimed — that is
 * the explicit "you still need to pick a name" state the API sends (§10 Phase 2).
 */
export interface Me {
  userId: string;
  /** The account's registered sign-in address — private, never public. */
  email: string;
  handle: string | null;
  displayName: string;
  bio: string | null;
  avatar: AvatarView | null;
  /** My drawn sign-off (an SVG path of pen strokes), or null. */
  signature: string | null;
  /** True until the first-run tour has been finished or set aside (once per account). */
  onboardingPending: boolean;
  /** The Getting started card: what is done so far, or null once it is put away. */
  gettingStarted: GettingStarted | null;
  emailVerified: boolean;
  /** A verification email went out and has not been used yet (never true for older accounts). */
  verificationPending: boolean;
  /**
   * When I was born, `YYYY-MM-DD` — mine in full. Correspondents only ever
   * receive the day and month of it, so nobody can work out my age.
   */
  birthday: string | null;
}

/** What is done on the Getting started card (the card lists only what isn't). */
export interface GettingStarted {
  photo: boolean;
  bio: boolean;
  invited: boolean;
  /** A bio line copied or the story card shared (slice 3). */
  bioShared: boolean;
  /** Confirmed their email, or were never asked to (slice 4). */
  emailConfirmed: boolean;
  firstLetter: boolean;
}

/** A quiet in-app line, shown once (slice 4). */
export interface Notice {
  id: string;
  kind: string;
  body: string;
  at: string;
}

/** A person found by handle search — how you start a correspondence. */
export interface PersonResult {
  handle: string;
  displayName: string;
  bio: string | null;
  avatar: AvatarView | null;
}

export type SetAvatarInput =
  | { kind: 'seal'; id: string }
  | { kind: 'face'; id: string }
  | { kind: 'upload'; data: string; mime: string };

export const profilesApi = createApi({
  reducerPath: 'profilesApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api/profiles`, credentials: 'include' }),
  tagTypes: ['Me', 'Notices'],
  endpoints: (b) => ({
    me: b.query<Me, void>({ query: () => '/me', providesTags: ['Me'] }),
    claimHandle: b.mutation<Me, { handle: string }>({
      query: (body) => ({ url: '/handle', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),
    /** Edit display name, bio and birthday (PATCH /me). Handle never changes. */
    updateProfile: b.mutation<Me, { displayName?: string; bio?: string; birthday?: string | null }>({
      query: (body) => ({ url: '/me', method: 'PATCH', body }),
      invalidatesTags: ['Me'],
    }),
    /** The first-run tour is done (finished or "Later") — it never shows again. */
    completeOnboarding: b.mutation<{ onboardingPending: false }, void>({
      query: () => ({ url: '/me/onboarding', method: 'POST' }),
      invalidatesTags: ['Me'],
    }),
    /** Getting started: someone was invited (a link shared, an invite sent). */
    markInvited: b.mutation<void, void>({
      query: () => ({ url: '/me/getting-started/invited', method: 'POST' }),
      invalidatesTags: ['Me'],
    }),
    /** Getting started: "Put your toiyp in your bio" — a bio line copied, the story card shared. */
    markBioShared: b.mutation<void, void>({
      query: () => ({ url: '/me/getting-started/bio-shared', method: 'POST' }),
      invalidatesTags: ['Me'],
    }),
    /** Getting started: "Hide this", for good. */
    hideGettingStarted: b.mutation<{ gettingStarted: null }, void>({
      query: () => ({ url: '/me/getting-started/hide', method: 'POST' }),
      invalidatesTags: ['Me'],
    }),
    notices: b.query<Notice[], void>({ query: () => '/me/notices', providesTags: ['Notices'] }),
    seeNotice: b.mutation<void, string>({
      query: (id) => ({ url: `/me/notices/${id}/seen`, method: 'POST' }),
      invalidatesTags: ['Notices'],
    }),
    search: b.query<PersonResult[], string>({
      query: (q) => ({ url: '/search', params: { q } }),
    }),
    /** Set the avatar — a letter-styled seal, or an uploaded square image. */
    setAvatar: b.mutation<Me, SetAvatarInput>({
      query: (body) => ({ url: '/avatar', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),
    /** Save the sign-off drawn in settings; every letter sealed after carries it. */
    setSignature: b.mutation<{ signature: string }, { path: string }>({
      query: (body) => ({ url: '/me/signature', method: 'PUT', body }),
      invalidatesTags: ['Me'],
    }),
    /** Clear it. Letters already sent keep theirs. */
    clearSignature: b.mutation<{ signature: null }, void>({
      query: () => ({ url: '/me/signature', method: 'DELETE' }),
      invalidatesTags: ['Me'],
    }),
    /** Remove the avatar — the deterministic initial circle returns. */
    clearAvatar: b.mutation<{ avatar: null }, void>({
      query: () => ({ url: '/avatar', method: 'DELETE' }),
      invalidatesTags: ['Me'],
    }),
  }),
});

export const {
  useMeQuery,
  useClaimHandleMutation,
  useUpdateProfileMutation,
  useCompleteOnboardingMutation,
  useMarkInvitedMutation,
  useMarkBioSharedMutation,
  useNoticesQuery,
  useSeeNoticeMutation,
  useHideGettingStartedMutation,
  useSearchQuery,
  useSetAvatarMutation,
  useClearAvatarMutation,
  useSetSignatureMutation,
  useClearSignatureMutation,
} = profilesApi;